/**
 * Surveys API client.
 *
 * v3.5.10 — Migrated to the shared axios instance from /lib/api.js.
 * That instance provides:
 *   • Request interceptor for Authorization: Bearer {yulontap_token}
 *   • Response interceptor that normalizes FastAPI 422 errors so
 *     `err.response.data.detail` is always a readable string (never an
 *     array of {type,loc,msg,input,ctx} objects). Prevents silent
 *     toast failures and "Objects are not valid as a React child" crashes.
 */
import api from '../../lib/api';

export const surveysAPI = {
    status: () => api.get('/surveys/_status').then(r => r.data),

    listTemplates: () => api.get('/surveys/templates').then(r => r.data),

    list: () => api.get('/surveys').then(r => r.data),

    get: (id) => api.get(`/surveys/${id}`).then(r => r.data),

    create: (payload) => api.post('/surveys', payload).then(r => r.data),

    duplicate: (id) => api.post(`/surveys/${id}/duplicate`).then(r => r.data),

    update: (id, payload) => api.put(`/surveys/${id}`, payload).then(r => r.data),

    remove: (id) => api.delete(`/surveys/${id}`).then(r => r.data),

    listResponses: (id, { limit = 50, skip = 0 } = {}) =>
        api.get(`/surveys/${id}/responses`, { params: { limit, skip } }).then(r => r.data),

    analytics: (id, { sinceDays = 30 } = {}) =>
        api.get(`/surveys/${id}/analytics`, { params: { since_days: sinceDays } }).then(r => r.data),

    // v3.5.20 — CSV export. Returns a Blob so the caller can trigger a download.
    exportResponsesCsv: (id) =>
        api.get(`/surveys/${id}/responses/export.csv`, { responseType: 'blob' }).then(r => r.data),

    cleanupResponses: (id, olderThanDays) =>
        api.post(`/surveys/${id}/responses/cleanup`, null, {
            params: { older_than_days: olderThanDays },
        }).then(r => r.data),

    // Public — no auth required for normal respondents.
    // v3.5.8 — Opportunistically attach Bearer token if an admin is browsing
    // in the same session (the request interceptor already does this).
    // Normal respondents (no token) are unaffected.
    publicGet: (slug) => api.get(`/surveys/public/${slug}`).then(r => r.data),

    publicSubmit: (slug, payload) =>
        api.post(`/surveys/public/${slug}/respond`, payload).then(r => r.data),

    // Optional respondent photo upload (camera or library). Returns { id }.
    publicUploadPhoto: (slug, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/surveys/public/${slug}/photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(r => r.data);
    },

    // Admin — fetch a response photo as an authenticated blob (never public).
    responsePhotoBlob: (surveyId, responseId, photoId) =>
        api.get(`/surveys/${surveyId}/responses/${responseId}/photo/${photoId}`, { responseType: 'blob' }).then(r => r.data),

    // v3.3.1 — translation job metrics (admin-only)
    translationMetrics: () => api.get('/surveys/_translation_metrics').then(r => r.data),

    // P_GUIDED_SURVEY_EXPERIENCES_1.0 — guided wizard endpoints.
    // Generated surveys land in the same MongoDB collection and use
    // the same Builder lifecycle — these endpoints are just a
    // different way to populate the engine.
    guided: {
        listOutcomes: () => api.get('/surveys/guided/outcomes').then(r => r.data),
        getOutcome: (id) => api.get(`/surveys/guided/outcomes/${id}`).then(r => r.data),
        instantiate: (payload) => api.post('/surveys/guided/instantiate', payload).then(r => r.data),
        // Honest readiness probe — driven by translation_meta.status.
        // The progress UX polls this until ready=true.
        status: (surveyId) => api.get(`/surveys/guided/status/${surveyId}`).then(r => r.data),
    },

    // P_SURVEY_INTELLIGENCE_ENGINE_1.0 — Slice A. Insight Campaigns
    // launcher endpoints. A campaign is a business-outcome-named
    // wrapper around a Guided Survey recipe + a five-facet "explain
    // itself" block. Launching a campaign goes through
    // `guided.instantiate` — campaigns add metadata, not a new engine.
    campaigns: {
        list: () => api.get('/surveys/campaigns').then(r => r.data),
        get: (id) => api.get(`/surveys/campaigns/${id}`).then(r => r.data),
    },
};

export default surveysAPI;
